/* Optional ECB reference conversions. Original Shopify USD amounts remain authoritative. */
(() => {
  const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'SGD'];
  const DAY = 86400000, MAX_AGE = 7 * DAY, CACHE_AGE = 12 * 3600000;
  const ENDPOINT = 'https://api.frankfurter.dev/v2/providers/ecb/rates?base=USD&quotes=EUR,GBP,JPY,CAD,AUD,SGD';
  const validateRates = (rows, now = Date.now()) => {
    if (!Array.isArray(rows) || !rows.length || rows.length > CURRENCIES.length - 1) return null;
    const rates = { USD: 1 };
    let date;
    for (const row of rows) {
      if (!row || row.base !== 'USD' || !CURRENCIES.includes(row.quote) || row.quote === 'USD'
        || Object.hasOwn(rates, row.quote) || typeof row.rate !== 'number' || !Number.isFinite(row.rate) || row.rate <= 0
        || !/^\d{4}-\d{2}-\d{2}$/.test(row.date)) return null;
      const sourceTime = Date.parse(`${row.date}T00:00:00Z`);
      if (!Number.isFinite(sourceTime) || new Date(sourceTime).toISOString().slice(0, 10) !== row.date
        || sourceTime > now || now - sourceTime >= MAX_AGE || (date && date !== row.date)) return null;
      date = row.date;
      rates[row.quote] = row.rate;
    }
    return { date, rates };
  };
  const estimate = (cents, currency, data, locale = 'en', now = Date.now()) => {
    const sourceTime = Date.parse(`${data?.date}T00:00:00Z`);
    if (!Number.isSafeInteger(cents) || cents < 0 || !CURRENCIES.includes(currency) || currency === 'USD'
      || !/^\d{4}-\d{2}-\d{2}$/.test(data?.date) || !Number.isFinite(sourceTime)
      || new Date(sourceTime).toISOString().slice(0, 10) !== data.date || sourceTime > now || now - sourceTime >= MAX_AGE
      || !Number.isFinite(data.rates?.[currency]) || data.rates[currency] <= 0) return null;
    const amount = cents / 100 * data.rates[currency];
    if (!Number.isFinite(amount)) return null;
    try { return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'code' }).format(amount); }
    catch { return null; }
  };
  globalThis.ROAMUNO_REFERENCE_MONEY = { validateRates, estimate, currencies: CURRENCIES };
  if (typeof document === 'undefined') return;

  const config = document.querySelector('[data-reference-config]');
  if (!config || document.documentElement.dataset.roamunoCurrency !== 'USD') return;
  const locale = document.documentElement.lang || 'en';
  const storageKey = 'roamuno-reference-currency-v1', cacheKey = 'roamuno-ecb-reference-v1';
  const buttons = [...document.querySelectorAll('[data-currency-choice]')];
  const status = document.querySelector('[data-reference-status]');
  let preference = 'USD', draft = 'USD', data = null, fetchedAt = 0, pending, lastAttempt = 0, expiryTimer;
  try {
    const saved = localStorage.getItem(storageKey);
    if (CURRENCIES.includes(saved)) preference = saved;
    const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
    if (cached && Number.isFinite(cached.fetchedAt) && cached.fetchedAt <= Date.now()
      && Date.now() - cached.fetchedAt < CACHE_AGE) {
      data = validateRates(cached.rows);
      if (data) fetchedAt = cached.fetchedAt;
    }
  } catch { /* Storage restrictions do not affect USD browsing or checkout. */ }
  const usable = () => data && Date.now() - Date.parse(`${data.date}T00:00:00Z`) < MAX_AGE;
  const activeCurrency = () => usable() && data.rates[preference] ? preference : 'USD';
  const updateButtons = () => {
    if (draft !== 'USD' && (!usable() || !data.rates[draft])) draft = 'USD';
    buttons.forEach(button => {
      button.disabled = button.dataset.currencyChoice !== 'USD' && (!usable() || !data.rates[button.dataset.currencyChoice]);
      button.setAttribute('aria-pressed', String(button.dataset.currencyChoice === draft));
    });
  };
  const render = () => {
    const currency = activeCurrency();
    document.querySelectorAll('[data-reference-currency-label]').forEach(label => {
      if (label.textContent !== currency) label.textContent = currency;
    });
    document.querySelectorAll('[data-reference-cents]').forEach(price => {
      const raw = price.dataset.referenceCents;
      const text = raw?.trim() ? estimate(Number(raw), currency, data, locale) : null;
      const value = text ? config.dataset.estimate.replaceAll('%%AMOUNT%%', text) : '';
      if (price.textContent !== value) price.textContent = value;
      price.hidden = !value;
    });
  };
  const updateStatus = () => {
    if (!status) return;
    const date = usable() ? new Intl.DateTimeFormat(locale, { timeZone: 'UTC' }).format(new Date(`${data.date}T00:00:00Z`)) : null;
    status.textContent = date ? config.dataset.updated.replaceAll('%%DATE%%', date) : config.dataset.unavailable;
  };
  const scheduleExpiry = () => {
    clearTimeout(expiryTimer);
    if (usable()) expiryTimer = setTimeout(() => { data = null; updateButtons(); updateStatus(); render(); },
      Math.max(0, Date.parse(`${data.date}T00:00:00Z`) + MAX_AGE - Date.now()));
  };
  const loadRates = async () => {
    if (pending) return pending;
    if (usable() && Date.now() - fetchedAt < CACHE_AGE) { updateButtons(); updateStatus(); scheduleExpiry(); return; }
    // No polling or automatic retries: revisit after cache TTL, or reopen after a bounded cooldown.
    if (Date.now() - lastAttempt < 60000) return;
    lastAttempt = Date.now();
    if (status) status.textContent = config.dataset.loading;
    pending = (async () => {
      const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await fetch(ENDPOINT, { credentials: 'omit', referrerPolicy: 'no-referrer', signal: controller.signal });
        if (!response.ok) throw new Error('reference_unavailable');
        const rows = await response.json(), valid = validateRates(rows);
        if (!valid) throw new Error('reference_invalid');
        data = valid; fetchedAt = Date.now();
        try { localStorage.setItem(cacheKey, JSON.stringify({ fetchedAt, rows })); } catch { /* Optional cache. */ }
      } catch {
        data = null;
      } finally {
        clearTimeout(timeout); pending = null;
        updateButtons(); updateStatus(); scheduleExpiry(); render();
      }
    })();
    return pending;
  };
  globalThis.ROAMUNO_REFERENCE_CURRENCY = {
    setPreference(currency) {
      preference = CURRENCIES.includes(currency) && (currency === 'USD' || (usable() && data.rates[currency])) ? currency : 'USD';
      draft = preference;
      try { localStorage.setItem(storageKey, preference); } catch { /* Optional preference. */ }
      updateButtons(); render();
    },
  };
  buttons.forEach(button => button.addEventListener('click', () => { draft = button.dataset.currencyChoice; updateButtons(); }));
  try {
    const names = new Intl.DisplayNames([locale], { type: 'currency' });
    document.querySelectorAll('[data-reference-currency-name]').forEach(name => { name.textContent = names.of(name.dataset.referenceCurrencyName); });
  } catch { /* ISO codes remain readable on browsers without DisplayNames. */ }
  document.querySelectorAll('[data-dialog-open="preferences"]').forEach(button => button.addEventListener('click', () => {
    draft = activeCurrency(); updateButtons(); void loadRates();
  }));
  let frame;
  const observer = new MutationObserver(() => {
    if (!frame) frame = requestAnimationFrame(() => { frame = null; render(); });
  });
  const main = document.querySelector('main');
  if (main) observer.observe(main, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-reference-cents'] });
  const onReturn = () => {
    if (document.visibilityState !== 'visible') return;
    render(); updateButtons(); updateStatus();
    if (preference !== 'USD') void loadRates();
  };
  window.addEventListener('pageshow', onReturn);
  window.addEventListener('focus', onReturn);
  document.addEventListener('visibilitychange', onReturn);
  draft = activeCurrency(); updateButtons(); updateStatus(); render(); scheduleExpiry();
  if (preference !== 'USD') void loadRates();
})();
